import { useCurrentUser, authClient, useSession } from '@/auth'
import { useRouter } from '@/hooks/router'
import { initI18n } from '@/i18n'
import { routes } from '@/routes'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import { useState, useEffect, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { User, Mail, Check, Loader2, Bell, BellOff } from 'lucide-react'
import { toast } from 'sonner'
import { useStore } from '@/hooks/store'
import { config } from '@/config/config'

export const TabLink = observer(function TabLink({
  tabName,
  children,
}: {
  tabName: string
  children: React.ReactNode
}) {
  const router = useRouter()
  const isActive = router.router.queryParams?.tab === tabName
  return (
    <a
      data-test-id={`settings-tab-${tabName}`}
      className={`inline-flex items-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&amp;_svg]:pointer-events-none [&amp;_svg]:size-4 [&amp;_svg]:shrink-0 h-10 px-4 py-2 w-full sm:w-auto lg:w-full justify-center lg:justify-start ${
        isActive
          ? 'bg-muted text-foreground shadow-sm'
          : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
      }`}
      onClick={(e) => {
        e.preventDefault()
        router.navigateToSettings({ tab: tabName })
      }}
      href={`/settings?tab=${tabName}`}
    >
      {children}
    </a>
  )
})

export const SettingsPage = observer(function SettingsPage() {
  const router = useRouter()
  const user = useCurrentUser()
  const { t } = useTranslation()

  const tab = router.router.queryParams?.tab || 'profile'
  return (
    <div data-test-id="settings-page" className="w-full max-w-[900px] mx-auto">
      <section className="px-4 py-6">
        <div className="mx-auto pb-4">
          <div>
            <h2 data-test-id="settings-title" className="text-2xl font-bold tracking-tight">
              {t('settings.title')}
            </h2>
            <p className="text-muted-foreground">{t('settings.description')}</p>
          </div>
          <div
            data-orientation="horizontal"
            role="none"
            className="shrink-0 bg-border h-[1px] w-full my-6"
          ></div>
          <div className="flex flex-col gap-6 lg:flex-row lg:gap-10">
            <aside className="w-full lg:w-56">
              <nav className="flex w-full flex-row flex-wrap gap-2 rounded-2xl border border-border bg-card p-2 shadow-sm lg:flex-col lg:rounded-none lg:border-0 lg:bg-transparent lg:p-0 lg:shadow-none">
                <TabLink tabName="preferences">
                  {t('settings.preferences')}
                </TabLink>
                <TabLink tabName="profile">{t('settings.profile')}</TabLink>
                {/* {org.organization && (
                  <TabLink tabName="organization">
                    {t('settings.organization')}
                  </TabLink>
                )} */}
              </nav>
            </aside>
            <div className="flex-1 w-full pb-16">
              <TabView />
            </div>
          </div>
        </div>
      </section>
    </div>
  )
})

export const TabView = observer(function TabView() {
  const router = useRouter()
  const tab = router.router.queryParams?.tab || 'profile'

  if (tab === 'profile') {
    return <ProfilePage />
  }

  if (tab === 'organization') {
    return <div>organization</div>
  }

  if (tab === 'preferences') {
    return <PreferencesPage />
  }

  return null
})

export const ProfilePage = observer(function ProfilePage() {
  const { t } = useTranslation()
  const user = useCurrentUser()
  const { refetch } = useSession()

  const [name, setName] = useState(user.name)
  const [isLoading, setIsLoading] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value)
    setHasChanges(e.target.value !== user.name)
  }

  const handleSave = async () => {
    if (!hasChanges || !name.trim()) return

    setIsLoading(true)
    try {
      const result = await authClient.updateUser({ name: name.trim() })

      if (result.error) {
        toast.error(result.error.message || t('profile.updateError'))
      } else {
        await refetch()
        setHasChanges(false)
        toast.success(t('profile.updateSuccess'))
      }
    } catch (err) {
      console.error('Update user error:', err)
      toast.error(t('profile.updateError'))
    } finally {
      setIsLoading(false)
    }
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  return (
    <div data-test-id="profile-tab-content" className="w-full max-w-2xl">
      <h1 className="font-bold text-xl mb-6">{t('settings.profile')}</h1>

      {/* Profile Card */}
      <div className="bg-card rounded-2xl shadow-sm border border-border overflow-hidden">
        {/* Header with avatar */}
        <div className="bg-forest p-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white text-xl font-semibold">
              {getInitials(user.name)}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">{user.name}</h2>
              <p className="text-white/80 text-sm">{user.email}</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="p-6 space-y-6">
          {/* Name field */}
          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-medium">
              {t('profile.name')}
            </Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                id="name"
                data-test-id="profile-name-input"
                type="text"
                value={name}
                onChange={handleNameChange}
                placeholder={t('profile.namePlaceholder')}
                disabled={isLoading}
                className="pl-10 h-12 rounded-xl"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {t('profile.nameDescription')}
            </p>
          </div>

          {/* Email field (read-only) */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium">
              {t('profile.email')}
            </Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                id="email"
                data-test-id="profile-email-input"
                type="email"
                value={user.email}
                disabled
                className="pl-10 h-12 rounded-xl bg-muted cursor-not-allowed"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {t('profile.emailDescription')}
            </p>
          </div>

          {/* Save button */}
          <div className="pt-4 border-t border-border">
            <Button
              data-test-id="profile-save-button"
              onClick={handleSave}
              disabled={!hasChanges || isLoading || !name.trim()}
              className="h-11 px-6 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('profile.saving')}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  {t('profile.saveChanges')}
                </span>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
})

type NotificationStatus =
  | 'loading'
  | 'unsupported'
  | 'prompt'
  | 'granted'
  | 'denied'

// Cache clientId to avoid regenerating on re-renders
let cachedClientId: string | null = null

function resolveClientId() {
  if (cachedClientId) {
    return cachedClientId
  }

  const storedClientId =
    typeof window !== 'undefined' ? localStorage.getItem('clientId') : null

  if (storedClientId) {
    cachedClientId = storedClientId
    return cachedClientId
  }

  const newClientId = crypto.randomUUID()
  cachedClientId = newClientId

  if (typeof window !== 'undefined') {
    queueMicrotask(() => {
      try {
        localStorage.setItem('clientId', newClientId)
      } catch {
        // Ignore storage errors (e.g., quota exceeded, disabled storage)
      }
    })
  }

  return newClientId
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')

  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }

  return outputArray
}

async function getLocalSubscriptionStatus(): Promise<'unsupported' | 'prompt' | 'granted' | 'denied'> {
  const swRegistration = await navigator.serviceWorker.getRegistration()
  if (!swRegistration) return 'unsupported'

  const pushManager = swRegistration.pushManager
  const permissionState = await pushManager.permissionState({
    userVisibleOnly: true,
  })

  return permissionState
}

async function subscribeToPush() {
  const VAPID_PUBLIC_KEY = config.vapidKey
  if (!VAPID_PUBLIC_KEY) return null

  const swRegistration = await navigator.serviceWorker.getRegistration()
  if (!swRegistration) return null

  const pushManager = swRegistration.pushManager

  // Reuse existing subscription if one is already present to avoid InvalidStateError
  const existingSubscription = await pushManager.getSubscription()
  if (existingSubscription) {
    return existingSubscription
  }

  const applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
  const subscription = await pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: applicationServerKey as BufferSource,
  })

  return subscription
}

export const NotificationSettings = observer(function NotificationSettings() {
  const { t } = useTranslation()
  const store = useStore()
  const clientId = resolveClientId()

  const [status, setStatus] = useState<NotificationStatus>('loading')
  const [hasServerSubscription, setHasServerSubscription] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    async function checkStatus() {
      try {
        const localStatus = await getLocalSubscriptionStatus()
        setStatus(localStatus)

        if (localStatus === 'granted') {
          try {
            const result = await store.provider.getPushSubscription(clientId)
            // Check if we got a valid subscription (API returns error object on 404)
            if (result && result.payload) {
              setHasServerSubscription(true)
            } else {
              setHasServerSubscription(false)
            }
          } catch {
            setHasServerSubscription(false)
          }
        }
      } catch {
        setStatus('unsupported')
      }
    }
    checkStatus()
  }, [clientId, store.provider])

  const handleEnable = useCallback(async () => {
    setIsLoading(true)
    try {
      const subscription = await subscribeToPush()
      if (subscription) {
        await store.provider.updatePushSubscription(
          clientId,
          JSON.stringify(subscription)
        )
        setStatus('granted')
        setHasServerSubscription(true)
        toast.success(t('notifications.enableSuccess'))
      } else {
        // subscribeToPush returned null (no service worker or VAPID key)
        setStatus('unsupported')
        toast.error(t('notifications.enableError'))
      }
    } catch (error) {
      console.error('Failed to enable notifications:', error)
      toast.error(t('notifications.enableError'))
      // Re-check status in case permission was denied
      try {
        const localStatus = await getLocalSubscriptionStatus()
        setStatus(localStatus)
      } catch {
        setStatus('unsupported')
      }
    } finally {
      setIsLoading(false)
    }
  }, [clientId, store.provider, t])

  const handleDisable = useCallback(async () => {
    setIsLoading(true)
    try {
      // First, attempt to unsubscribe from any existing PushSubscription in the browser
      if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration()
        if (registration) {
          const subscription = await registration.pushManager.getSubscription()
          if (subscription) {
            await subscription.unsubscribe()
          }
        }
      }

      // Then remove the subscription record on the server
      await store.provider.deletePushSubscription(clientId)
      setHasServerSubscription(false)

      // Re-check local subscription/permission status to keep UI in sync
      try {
        const localStatus = await getLocalSubscriptionStatus()
        setStatus(localStatus)
      } catch {
        setStatus('unsupported')
      }

      toast.success(t('notifications.disableSuccess'))
    } catch (error) {
      console.error('Failed to disable notifications:', error)
      toast.error(t('notifications.disableError'))
    } finally {
      setIsLoading(false)
    }
  }, [clientId, store.provider, t])

  const renderContent = () => {
    if (status === 'loading') {
      return (
        <div
          data-test-id="notifications-loading"
          className="flex items-center gap-2 text-muted-foreground"
        >
          <Loader2 className="w-4 h-4 animate-spin" />
        </div>
      )
    }

    if (status === 'unsupported') {
      return (
        <p
          data-test-id="notifications-unsupported"
          className="text-sm text-muted-foreground"
        >
          {t('notifications.unsupported')}
        </p>
      )
    }

    if (status === 'denied') {
      return (
        <p
          data-test-id="notifications-denied"
          className="text-sm text-muted-foreground"
        >
          {t('notifications.denied')}
        </p>
      )
    }

    if (status === 'granted' && hasServerSubscription) {
      return (
        <div data-test-id="notifications-configured" className="space-y-3">
          <p className="text-sm text-success flex items-center gap-2">
            <Bell className="w-4 h-4" />
            {t('notifications.configured')}
          </p>
          <Button
            data-test-id="notifications-disable-button"
            variant="outline"
            size="sm"
            onClick={handleDisable}
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <BellOff className="w-4 h-4 mr-2" />
            )}
            {t('notifications.disable')}
          </Button>
        </div>
      )
    }

    // status === 'prompt' or (status === 'granted' && !hasServerSubscription)
    return (
      <div data-test-id="notifications-not-configured" className="space-y-3">
        <p className="text-sm text-muted-foreground">
          {t('notifications.notConfigured')}
        </p>
        <Button
          data-test-id="notifications-enable-button"
          variant="default"
          size="sm"
          onClick={handleEnable}
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <Bell className="w-4 h-4 mr-2" />
          )}
          {t('notifications.enable')}
        </Button>
      </div>
    )
  }

  return (
    <div data-test-id="notification-settings" className="space-y-2">
      <h2 className="font-semibold text-lg">{t('notifications.title')}</h2>
      {renderContent()}
    </div>
  )
})

export const PreferencesPage = observer(function PreferencesPage() {
  const { t } = useTranslation()
  return (
    <div data-test-id="preferences-tab-content" className="space-y-8">
      <div>
        <h1 className="font-bold text-xl mb-4">{t('settings.preferences')}</h1>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <Label htmlFor="language">{t('settings.language')}</Label>
          <select
            data-test-id="language-select"
            name="language"
            id="language"
            defaultValue={localStorage.getItem('language') || 'en'}
            onChange={(e) => {
              const language = e.target.value

              localStorage.setItem('language', language)
              initI18n(language)
            }}
            className="w-full sm:w-56 rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="en">English</option>
            <option value="pl">Polski</option>
          </select>
        </div>
      </div>

      <NotificationSettings />
    </div>
  )
})
